import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { prepareHeaders } from '../configuration/tokenHandling/prepareHeaders';
import { config } from '../config';
import { knownEulaStatus, type KnownEulaStatus } from '../features/man-eula/config/types';

export interface ManEulaStatus {
    account_id: string;
    status: KnownEulaStatus;
    channel?: string;
    eula_version?: string;
    event_timestamp?: string;
    legal_principal?: Record<string, string>;
    authenticated_subject?: string;
    client_id?: string;
    vin_snapshot?: string[];
    etag: string;
}

const decidedEulaStatuses = {
    [knownEulaStatus.ACCEPTED]: true,
    [knownEulaStatus.DECLINED]: true,
    [knownEulaStatus.REVOKED]: true,
} satisfies Partial<Record<KnownEulaStatus, true>>;

type DecidedEulaStatus = keyof typeof decidedEulaStatuses;

export interface DecidedManEulaStatus extends ManEulaStatus {
    status: DecidedEulaStatus;
}

export const isDecidedManEulaStatus = (eulaStatus: ManEulaStatus): eulaStatus is DecidedManEulaStatus => {
    return eulaStatus.status in decidedEulaStatuses;
};

export interface Account {
    id: string;
    name: string;
    legalAddress: {
        line1?: string;
        line2?: string;
        line3?: string;
        postalCode?: string;
        city?: string;
        countryCode: string;
    };
}

export const iamApi = createApi({
    reducerPath: 'iamApi',
    baseQuery: fetchBaseQuery({
        baseUrl: config.backend.IAM_API,
        prepareHeaders,
    }),
    tagTypes: ['Eula'],
    endpoints: builder => ({
        fetchEulaStatus: builder.query<ManEulaStatus, string>({
            query: accountId => ({
                url: `/accounts/${accountId}/man-eula-status`,
                responseHandler: async (response: Response) => {
                    const data = await response.json();
                    return {
                        ...data,
                        etag: response.headers.get('ETag') || '',
                    };
                },
            }),
            // keepUnusedDataFor: 10,
            providesTags: ['Eula'],
        }),

        fetchAccount: builder.query<Account, string>({
            query: accountId => ({
                url: `/accounts/${accountId}`,
                responseHandler: async (response: Response) => {
                    const data = await response.json();
                    return {
                        id: data.id,
                        name: data.name,
                        legalAddress: {
                            line1: data.legal_address.line_1,
                            city: data.legal_address.city,
                            postalCode: data.legal_address.postal_code,
                            countryCode: data.legal_address.country_code,
                        },
                    };
                },
            }),
        }),

        updateEulaStatus: builder.mutation<
            void,
            {
                accountId: string;
                etag: string;
                status: string;
                channel: string;
                eulaVersion: string;
                firstName: string;
                lastName: string;
                contactInfo: string;
                profileId: string;
                vinSnapshot: string[];
            }
        >({
            query: ({ accountId, etag, ...update }) => ({
                url: `/accounts/${accountId}/man-eula-status`,
                method: 'POST',
                headers: new Headers([['If-Match', etag]]),
                body: {
                    status: update.status,
                    channel: update.channel,
                    eula_version: update.eulaVersion,
                    legal_principal: {
                        first_name: update.firstName,
                        last_name: update.lastName,
                        contact_info: update.contactInfo,
                        rio_user_profile_id: update.profileId,
                    },
                    vin_snapshot: update.vinSnapshot,
                },
            }),
            invalidatesTags: ['Eula'],
        }),
    }),
});

export const { useFetchEulaStatusQuery, useFetchAccountQuery, useUpdateEulaStatusMutation } = iamApi;
